import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { baseURL } from "../utils/common";

const fieldOrder = [
    "client_type",
    "name",
    "bin_code",
    "address",
    "contact_name",
    "contact_phone_number",
    "contact_email",
];

export const ClientDetailsPage = () => {
    const { id } = useParams();
    const history = useNavigate();

    const CLIENT_DETAIL_URL = `${baseURL}/orders/client/${id}/details/`;
    const CLIENT_UPDATE_URL = `${baseURL}/orders/client/${id}/update/`;

    const [data, setData] = useState(null);
    const [fields, setFields] = useState(null);
    const [form, setForm] = useState({});

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [error, setError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveMessage, setSaveMessage] = useState("");

    const loadClient = async () => {
        setLoading(true);
        setError("");
        setSaveError("");
        setSaveMessage("");

        try {
            const detailResponse = await axios.get(CLIENT_DETAIL_URL);
            const detailData = detailResponse.data;

            if (!detailData.success) {
                setError(detailData.message || "Не удалось загрузить клиента");
                return;
            }

            setData(detailData);

            try {
                const updateResponse = await axios.get(CLIENT_UPDATE_URL);
                const updateData = updateResponse.data;

                if (updateData.success) {
                    const apiFields = updateData.fields || {};
                    const nextForm = {};

                    fieldOrder.forEach((key) => {
                        if (apiFields[key]) {
                            nextForm[key] = apiFields[key].value || apiFields[key].default || "";
                        }
                    });

                    setFields(apiFields);
                    setForm(nextForm);
                }
            } catch (e) {
                const client = detailData.client || {};
                const contact = detailData.main_contact || {};

                setFields({
                    client_type: {
                        type: "text",
                        label: "Тип клиента",
                        required: true,
                    },
                    name: {
                        type: "text",
                        label: "Название компании / Имя заказчика",
                        required: true,
                    },
                    bin_code: {
                        type: "text",
                        label: "БИН/ИИН",
                        required: false,
                    },
                    address: {
                        type: "textarea",
                        label: "Адрес",
                        required: true,
                    },
                    contact_name: {
                        type: "text",
                        label: "Контактное лицо",
                        required: false,
                    },
                    contact_phone_number: {
                        type: "tel",
                        label: "Телефон",
                        required: false,
                    },
                    contact_email: {
                        type: "email",
                        label: "Почта",
                        required: false,
                    },
                });

                setForm({
                    client_type: client.client_type !== "—" ? client.client_type || "" : "",
                    name: client.name !== "—" ? client.name || "" : "",
                    bin_code: client.bin_code !== "—" ? client.bin_code || "" : "",
                    address: client.address !== "—" ? client.address || "" : "",
                    contact_name: contact.name !== "—" ? contact.name || "" : "",
                    contact_phone_number: contact.phone_number !== "—" ? contact.phone_number || "" : "",
                    contact_email: contact.email !== "—" ? contact.email || "" : "",
                });
            }
        } catch (e) {
            setError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setLoading(false);
        }
    };

    const changeField = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const saveClient = async (e) => {
        e.preventDefault();

        if (saving) {
            return;
        }

        setSaving(true);
        setSaveError("");
        setSaveMessage("");

        try {
            const response = await axios.post(CLIENT_UPDATE_URL, form);
            const responseData = response.data;

            if (!responseData.success) {
                setSaveError(responseData.message || "Не удалось сохранить данные");
                return;
            }

            setSaveMessage(responseData.message || "Данные сохранены");

            await loadClient();
        } catch (e) {
            setSaveError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        loadClient();
    }, [id]);

    if (loading) {
        return (
            <section className="client-details-page">
                <div className="client-details-loading">Загрузка клиента...</div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="client-details-page">
                <button className="client-details-back" type="button" onClick={() => history("/clients")}>
                    ← Назад к клиентам
                </button>

                <div className="client-details-error">{error}</div>
            </section>
        );
    }

    const client = data?.client || {};
    const mainContact = data?.main_contact || {};
    const contacts = data?.contacts || [];
    const ordersSummary = data?.orders_summary || {};
    const orders = data?.orders || [];

    return (
        <section className="client-details-page">
            <div className="client-details-head">
                <div>
                    <button className="client-details-back" type="button" onClick={() => history("/clients")}>
                        ← Назад к клиентам
                    </button>

                    <h1>{client.name || "—"}</h1>
                    <p>{client.address || "—"}</p>
                </div>

                <div className="client-details-badge">
                    {client.client_type || "—"}
                </div>
            </div>

            <div className="client-details-grid">
                <div className="client-details-card client-details-main-card">
                    <div className="client-details-card-head">
                        <div>
                            <h2>Информация о клиенте</h2>
                            <p>Основные данные и контактное лицо</p>
                        </div>
                    </div>

                    <div className="client-details-info-grid">
                        <div className="client-details-info-item">
                            <span>ID клиента</span>
                            <strong>#{client.id || "—"}</strong>
                        </div>

                        <div className="client-details-info-item">
                            <span>Тип клиента</span>
                            <strong>{client.client_type || "—"}</strong>
                        </div>

                        <div className="client-details-info-item">
                            <span>БИН/ИИН</span>
                            <strong>{client.bin_code || "—"}</strong>
                        </div>

                        <div className="client-details-info-item">
                            <span>Создан</span>
                            <strong>{client.created_at || "—"}</strong>
                        </div>

                        <div className="client-details-info-item">
                            <span>Обновлён</span>
                            <strong>{client.updated_at || "—"}</strong>
                        </div>

                        <div className="client-details-info-item">
                            <span>Контактное лицо</span>
                            <strong>{mainContact.name || "—"}</strong>
                        </div>

                        <div className="client-details-info-item">
                            <span>Телефон</span>

                            {mainContact.phone_number && mainContact.phone_number !== "—" ? (
                                <a href={`tel:${mainContact.phone_number}`}>
                                    {mainContact.phone_number}
                                </a>
                            ) : (
                                <strong>—</strong>
                            )}
                        </div>

                        <div className="client-details-info-item">
                            <span>Почта</span>

                            {mainContact.email && mainContact.email !== "—" ? (
                                <a href={`mailto:${mainContact.email}`}>
                                    {mainContact.email}
                                </a>
                            ) : (
                                <strong>—</strong>
                            )}
                        </div>
                    </div>
                </div>

                <div className="client-details-card client-details-summary-card">
                    <h2>Заказы</h2>

                    <div className="client-summary-number">
                        <span>Количество заказов</span>
                        <strong>{ordersSummary.count || 0}</strong>
                    </div>

                    <div className="client-summary-number">
                        <span>Общая сумма</span>
                        <strong>{Number(ordersSummary.pay_sum || 0).toLocaleString("ru-RU")} ₸</strong>
                    </div>
                </div>
            </div>

            <div className="client-details-card">
                <div className="client-details-card-head">
                    <div>
                        <h2>Изменить данные</h2>
                        <p>После сохранения данные обновятся на странице</p>
                    </div>
                </div>

                {saveError && <div className="client-details-error">{saveError}</div>}
                {saveMessage && <div className="client-details-success">{saveMessage}</div>}

                {fields && (
                    <form className="client-edit-form" onSubmit={saveClient}>
                        {fieldOrder.map((key) => {
                            const field = fields[key];

                            if (!field) {
                                return null;
                            }

                            if (field.type === "select") {
                                return (
                                    <label key={key}>
                                        <span>
                                            {field.label}
                                            {field.required && <b>*</b>}
                                        </span>

                                        <select
                                            value={form[key] || ""}
                                            onChange={(e) => changeField(key, e.target.value)}
                                        >
                                            {field.options?.map((item) => (
                                                <option key={item.value} value={item.value}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }

                            if (field.type === "textarea") {
                                return (
                                    <label key={key} className="client-edit-form-full">
                                        <span>
                                            {field.label}
                                            {field.required && <b>*</b>}
                                        </span>

                                        <textarea
                                            value={form[key] || ""}
                                            onChange={(e) => changeField(key, e.target.value)}
                                            placeholder={field.placeholder || ""}
                                        />
                                    </label>
                                );
                            }

                            return (
                                <label key={key}>
                                    <span>
                                        {field.label}
                                        {field.required && <b>*</b>}
                                    </span>

                                    <input
                                        type={field.type || "text"}
                                        value={form[key] || ""}
                                        onChange={(e) => changeField(key, e.target.value)}
                                        placeholder={field.placeholder || ""}
                                    />
                                </label>
                            );
                        })}

                        <div className="client-edit-actions">
                            <button type="button" onClick={loadClient}>
                                Сбросить
                            </button>

                            <button type="submit" disabled={saving}>
                                {saving ? "Сохраняем..." : "Сохранить изменения"}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <div className="client-details-card">
                <div className="client-details-card-head">
                    <div>
                        <h2>Контакты</h2>
                        <p>Все контактные лица клиента</p>
                    </div>
                </div>

                {contacts.length === 0 && (
                    <div className="client-details-empty">Контактов нет</div>
                )}

                {contacts.length > 0 && (
                    <div className="client-contacts-list">
                        {contacts.map((contact) => (
                            <div className="client-contact-card" key={contact.id}>
                                <div>
                                    <span>Контактное лицо</span>
                                    <strong>{contact.name || "—"}</strong>
                                </div>

                                <div>
                                    <span>Телефон</span>
                                    {contact.phone_number && contact.phone_number !== "—" ? (
                                        <a href={`tel:${contact.phone_number}`}>
                                            {contact.phone_number}
                                        </a>
                                    ) : (
                                        <strong>—</strong>
                                    )}
                                </div>

                                <div>
                                    <span>Почта</span>
                                    {contact.email && contact.email !== "—" ? (
                                        <a href={`mailto:${contact.email}`}>
                                            {contact.email}
                                        </a>
                                    ) : (
                                        <strong>—</strong>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="client-details-card">
                <div className="client-details-card-head">
                    <div>
                        <h2>История заказов</h2>
                        <p>Все заказы, которые есть у клиента</p>
                    </div>
                </div>

                {orders.length === 0 && (
                    <div className="client-details-empty">Заказов нет</div>
                )}

                {orders.length > 0 && (
                    <div className="client-orders-list">
                        {orders.map((order) => (
                            <article className="client-order-card" key={order.id}>
                                <div className="client-order-top">
                                    <div>
                                        <span>Заказ</span>
                                        <h3>#{order.id}</h3>
                                    </div>

                                    <strong>{order.order_status || "—"}</strong>
                                </div>

                                <div className="client-order-grid">
                                    <div>
                                        <span>Сумма</span>
                                        <strong>{Number(order.pay_sum || 0).toLocaleString("ru-RU")} ₸</strong>
                                    </div>

                                    <div>
                                        <span>Менеджер</span>
                                        <strong>{order.manager?.name || "—"}</strong>
                                    </div>

                                    <div>
                                        <span>Создан</span>
                                        <strong>{order.created_at || "—"}</strong>
                                    </div>

                                    <div>
                                        <span>Обновлён</span>
                                        <strong>{order.updated_at || "—"}</strong>
                                    </div>

                                    <div>
                                        <span>Счёт</span>
                                        {order.invoice && order.invoice !== "—" ? (
                                            <a href={order.invoice} target="_blank" rel="noreferrer">
                                                Открыть
                                            </a>
                                        ) : (
                                            <strong>—</strong>
                                        )}
                                    </div>

                                    <div>
                                        <span>Оплата</span>
                                        {order.payment && order.payment !== "—" ? (
                                            <a href={order.payment} target="_blank" rel="noreferrer">
                                                Открыть
                                            </a>
                                        ) : (
                                            <strong>—</strong>
                                        )}
                                    </div>

                                    <div>
                                        <span>Накладная</span>
                                        {order.delivery_note && order.delivery_note !== "—" ? (
                                            <a href={order.delivery_note} target="_blank" rel="noreferrer">
                                                Открыть
                                            </a>
                                        ) : (
                                            <strong>—</strong>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
